import { type ReactNode } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Drawer, IconButton, Tooltip } from "@mui/material";
import clsx from "clsx";
import QuestionIcon from "@mui/icons-material/Help";
import SettingsIcon from "@mui/icons-material/Settings";

import expandIcon from "../assets/svg/close.svg";
import collapseIcon from "../assets/svg/open-panel.svg";
import type { RootState } from "../store/state/rootReducer";

import { makeStyles, useScreenSize } from "./theme/theme";
import { UploadingFilesProgress } from "./UploadingFilesProgress";
import { FooterActionBar } from "./FooterActionBar";

const useClasses = makeStyles<{
  isSmall: boolean;
  isExpanded: boolean;
  isCallPage: boolean;
  removePadding: boolean;
}>()((theme, { isSmall, isExpanded, isCallPage, removePadding }) => ({
  root: {
    display: "flex",
    height: "100%",
  },
  hide: {
    display: "none",
  },
  drawer: {
    flexShrink: 0,
    whiteSpace: "nowrap",
    borderWidth: 0,
    overflowX: "hidden",
    backgroundColor: "#F7F9FC",
    color: `${theme.palette.getContrastText(theme.palette.primary.main)} !important;
    transition: ${theme.transitions.create("width", {
      easing: isExpanded ? theme.transitions.easing.easeOut : theme.transitions.easing.easeIn,
      duration: theme.transitions.duration.standard,
    })} !important`,
  },
  drawerPaperOpen: {
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
    width: `${theme.drawerWidth}px`,
  },
  drawerPaperClose: {
    overflowX: "hidden",
    backgroundColor: "#F7F9FC",
    width: theme.spacing(7),
    [theme.breakpoints.down("sm")]: {
      width: theme.spacing(7 * theme.mobileScalingFactor),
    },
  },
  contentWrapper: {
    flexGrow: 1,
    backgroundColor: "#F7F9FC",
    width: `calc(100% - ${theme.drawerWidth}px)`,
    paddingTop: removePadding ? 0 : "16px",
    paddingRight: removePadding ? 0 : "16px",
    paddingBottom: removePadding ? 0 : "16px",
  },
  content: {
    height: "100%",
    overflowY: "auto",
    borderRadius: isCallPage ? "none" : "8px",
    backgroundColor: "#fff",
    position: "relative",
  },
  drawerContainer: {
    display: "flex",
    flexDirection: "column",
    boxShadow: "none",
    height: "100%",
    color: `${theme.palette.getContrastText(theme.palette.primary.main)} !important`,
  },
  drawerChildren: {
    flex: 1,
    alignSelf: "stretch",
  },
  expansionButton: {
    display: "flex",
    color: "#464C53",
    flexDirection: "column",
    paddingTop: 20 * (isSmall ? theme.mobileScalingFactor : 1),
    paddingBottom: 20 * (isSmall ? theme.mobileScalingFactor : 1),
    // Fixed at the collapsed-nav inset always — these 3 icons never move or
    // resize between expanded/collapsed, by design (they're icon-only, no label).
    paddingLeft: "14px",
    paddingRight: "14px",
  },
  expansionButtonGroup: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "2px",
  },
  expansionIconButton: {
    width: "36px",
  },
  collapseIcon: {
    fontSize: 20,
    marginRight: 10,
  },
  collapseText: {
    marginLeft: "auto",
    marginRight: "auto",
  },
  expandIcon: {
    fontSize: 20 * (isSmall ? theme.mobileScalingFactor : 1),
  },
  redbackground: {
    backgroundColor: "red",
  },
  appBar: {
    top: 0,
    left: 0,
    color: "#000",
    backgroundColor: "#fff",
    "& button": {
      marginRight: "4px",
    },
    "& button:focus": {
      outline: "none",
    },
    "& h1": {
      fontSize: "29px",
      fontWeight: 400,
      marginLeft: "8px",
    },
  },

  singleappBar: {
    top: 0,
    left: 0,
    color: "#000",
    backgroundColor: "#FFFFFF",
    textAlign: "left",
  },

  accountButton: {
    marginLeft: "auto",
    marginRight: 0,
  },
  menu: {
    "& hr": {
      marginTop: 8,
      marginBottom: 8,
    },
  },
  userPanel: {
    padding: "8px 16px",
    minWidth: 200,
    h5: {
      fontSize: 16,
      fontWeight: 700,
    },
    "& > *": {
      textAlign: "center",
    },
  },
  marginTopButton: {
    marginRight: "8px !important",
  },
}));

export interface IPageProps {
  children: ReactNode;
  sidebarElement: ReactNode;
  isExpanded: boolean;
  isUserImpersonated?: boolean;
  setExpanded: (expanded: boolean) => void;
}

export const MainLayout = ({ children, isExpanded, setExpanded, sidebarElement, isUserImpersonated }: IPageProps) => {
  const { isSmall } = useScreenSize();
  const isNotUser = useSelector((state: RootState) => !state.auth?.authenticated);
  const uploadingCloudFiles = JSON.parse(localStorage.getItem("uploadingFiles"));
  const { uploadingFiles: uploadingLocalFiles, projectId } = useSelector((state: RootState) => state.uploadFiles);
  const resolvedProjectId = projectId || uploadingCloudFiles?.projectId;
  const isNewConclude = useSelector((state: RootState) => state.featureFlags?.flags?.enableNewConclude);
  const isUploading = uploadingCloudFiles || uploadingLocalFiles;
  const navigate = useNavigate();
  const organizationInfo = useSelector((state: RootState) => state.auth.organization_info);
  const currentOrgCode = (organizationInfo as { organizational_code?: string })?.organizational_code ?? "";
  const canManageActiveOrganization = useSelector((state: RootState) => state.auth.can_manage_active_organization);

  const isCallPage =
    useLocation().pathname.includes("/room") ||
    useLocation().pathname.includes("/daily") ||
    useLocation().pathname.includes("/start") ||
    useLocation().pathname.includes("/call") ||
    useLocation().pathname.includes("/session") ||
    useLocation().pathname.includes("/interview_dolby") ||
    useLocation().pathname.includes("/author") ||
    useLocation().pathname.includes("/newEdit") ||
    useLocation().pathname.includes("/newConclude") ||
    useLocation().pathname.includes("/newUpload");

  const isAuthorPage = useLocation().pathname.includes("/author");
  const isMagicLink = useLocation().pathname.includes("/accept");
  const isConcludePage = useLocation().pathname.includes("/conclude");
  const isEditPageNewConclude = useLocation().pathname.endsWith("/edit") && isNewConclude;
  const removePadding = isCallPage || isConcludePage || isEditPageNewConclude || isNotUser;
  const { classes } = useClasses({
    isSmall,
    isExpanded,
    isCallPage,
    removePadding,
  });

  let showNavBar: boolean;

  if (isCallPage || isMagicLink || isConcludePage || isEditPageNewConclude || isAuthorPage || isNotUser) {
    showNavBar = false;
  } else {
    showNavBar = true;
  }

  const drawerPaperClass = clsx([
    classes.drawer,
    {
      [classes.drawerPaperOpen]: isExpanded,
      [classes.drawerPaperClose]: !isExpanded,
    },
  ]);

  const drawerContents = (
    <div className={clsx([classes.drawerContainer, isUserImpersonated && classes.redbackground])}>
      <div className={classes.drawerChildren}>{sidebarElement}</div>
      <div className={classes.expansionButton}>
        <>
          {isExpanded && isUploading && resolvedProjectId && (
            <Box
              sx={{ cursor: "pointer" }}
              onClick={() => navigate(`/projects/${resolvedProjectId}/uploadingMetadata`)}
            >
              <UploadingFilesProgress />
            </Box>
          )}
          <Box className={classes.expansionButtonGroup} justifyContent={"flex-start"} marginTop={"12px"}>
            <IconButton
              className={classes.expansionIconButton}
              sx={{
                color: "#606971",
                "&:focus": {
                  outline: "none",
                },
                borderRadius: "8px !important",
              }}
              onClick={() => setExpanded(!isExpanded)}
              size="small"
              style={{ padding: "8px" }}
            >
              <Tooltip title={isExpanded ? "Collapse Menu" : "Expand Menu"}>
                <img src={isExpanded ? expandIcon : collapseIcon} width="24px" />
              </Tooltip>
            </IconButton>

            <Tooltip title="Tutorials">
              <IconButton
                className={classes.expansionIconButton}
                color="primary"
                sx={{
                  color: "#606971",
                  "&:focus": {
                    outline: "none",
                  },
                  borderRadius: "8px !important",
                }}
                style={{ padding: "8px" }}
                size="small"
                onClick={() => window.open("https://theirstory.zendesk.com/", "_blank", "noopener,noreferrer")}
              >
                <QuestionIcon fontSize="medium" />
              </IconButton>
            </Tooltip>

            {canManageActiveOrganization && (
              <Tooltip title="Organization settings">
                <IconButton
                  className={classes.expansionIconButton}
                  sx={{
                    color: "#606971",
                    "&:focus": {
                      outline: "none",
                    },
                    borderRadius: "8px !important",
                  }}
                  style={{ padding: "8px" }}
                  size="small"
                  onClick={() => navigate(`/organization/${currentOrgCode}/org-settings`)}
                >
                  <SettingsIcon fontSize="medium" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </>
      </div>
    </div>
  );

  return (
    <>
      <div className={classes.root}>
        {showNavBar ? (
          isSmall ? (
            <>
              <Drawer
                variant="permanent"
                classes={{ paper: clsx([classes.drawer, classes.drawerPaperClose]) }}
                className={clsx([classes.drawer, classes.drawerPaperClose])}
              >
                {drawerContents}
              </Drawer>
              <Drawer
                anchor="left"
                classes={{ paper: clsx([classes.drawer, classes.drawerPaperOpen]) }}
                open={isExpanded}
                className={clsx([classes.drawer, classes.drawerPaperOpen])}
              >
                {drawerContents}
              </Drawer>
            </>
          ) : (
            <Drawer
              variant="permanent"
              className={clsx([drawerPaperClass])}
              classes={{
                paper: drawerPaperClass,
              }}
              open={isExpanded}
            >
              {drawerContents}
            </Drawer>
          )
        ) : null}

        <main className={classes.contentWrapper}>
          <div className={classes.content}>{children}</div>
          <FooterActionBar />
        </main>
      </div>
    </>
  );
};
